import { useMachine } from "@xstate/react";
import React from "react";
import { useParams } from "react-router-dom";
import { Row } from "reactstrap";
import { Spinner } from "src/components/Spinner";
import { RequestStatus, ReceiptErrorType, ErrorTypes } from "src/lib/api/types";
import { useApi } from "src/lib/ApiProvider";
import { navigationMachine } from "./navigation";
import axios from "axios";
import { ReceiptError } from "src/components/ReceiptError";
import { isErrorResponse } from "src/lib/api/utils";

import { ReceiptDetail } from "./ReceiptDetail";
import ReceiptDetailComplain from "./ReceiptDetailComplain";
import ReceiptDetailComplainConfirmation from "./ReceiptDetailComplainConfirmation";
import { ReceiptContextProvider } from "./ReceiptContext";

export const Receipt: React.FC = () => {
  const [current, send] = useMachine(navigationMachine);
  const { authApi } = useApi();
  const { sessionToken, orderId } = useParams();
  const [order, setOrder] = React.useState<ApiOrderDetail>();
  const [requestStatus, setRequestStatus] =
    React.useState<RequestStatus>("idle");
  const [errorType, setErrorType] = React.useState<ReceiptErrorType | null>(null);

  const fetchOrder = React.useCallback(
    async (orderId: string) => {
      setRequestStatus("loading");
      setErrorType(null);
      try {
        const response = await authApi.get<ApiOrderDetail>(
          `api/customer/v2/orders/${orderId}`
        );
        setOrder({
          ...response.data,
          draftCreatedAt:
            response.data.draftCreatedAt ?? response.data.createdAt,
        });
        setRequestStatus("success");
      } catch (error) {
        setRequestStatus("error");
        if (axios.isAxiosError(error)) {
          // use the error type from the server
          if (isErrorResponse(error)) {
            const serverErrorType = error.response.data.type;
            if (serverErrorType === ErrorTypes.ORDER_NOT_FOUND) {
              setErrorType(ReceiptErrorType.NOT_FOUND);
            } else {
              setErrorType(ReceiptErrorType.SERVER_ERROR);
            }
          } else if (error.response?.status === 404) {
            // if there is no error type information, fallback to using the HTTP status code
            setErrorType(ReceiptErrorType.NOT_FOUND);
          } else {
            setErrorType(ReceiptErrorType.SERVER_ERROR);
          }
        } else {
          setErrorType(ReceiptErrorType.SERVER_ERROR);
        }
      }
    },
    [authApi]
  );

  React.useEffect(() => {
    if (orderId && sessionToken) {
      fetchOrder(orderId);
    }
  }, [authApi, orderId, sessionToken, fetchOrder]);

  const onContest = React.useCallback(() => {
    send("CONTEST");
  }, [send]);

  const handleRetry = React.useCallback(() => {
    if (orderId) {
      fetchOrder(orderId);
    }
  }, [orderId, fetchOrder]);

  if (requestStatus === "error" && errorType) {
    return <ReceiptError errorType={errorType} onRetry={handleRetry} />;
  }

  if (requestStatus === "loading") {
    return (
      <Row className="justify-content-center" style={{ marginTop: "100px" }}>
        <Spinner />
      </Row>
    );
  }

  if (requestStatus === "success" && order) {
    return (
      <>
        <ReceiptContextProvider order={order} onContest={onContest}>
          {current.matches("detail") && <ReceiptDetail />}
          {current.matches("contest") && (
            <ReceiptDetailComplain
              onBack={() => {
                send("BACK");
                if (orderId) {
                  fetchOrder(orderId);
                }
              }}
              onContestSuccess={() => {
                send("CONTEST_MESSAGE");
              }}
            />
          )}
          {current.matches("contestMsg") && (
            <ReceiptDetailComplainConfirmation
              onBack={() => {
                if (orderId) {
                  fetchOrder(orderId);
                }
                send("BACK");
              }}
            />
          )}
        </ReceiptContextProvider>
      </>
    );
  }

  return (
    <Row className="justify-content-center" style={{ marginTop: "100px" }}>
      <Spinner />
    </Row>
  );
};
